import { AxiosRequestConfigMethods } from "@/api/types";
import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import { EnvironmentVariables } from "@/modules/common/types";
import {
  NotificationQueryParams,
  NotificationResponseDto,
  SystemNotificationTypeDto,
  PaginatedSystemNotificationTypesResponse,
} from "@/modules/notification-center/dtos";

const PAGE_SIZE = 50;
const YMIR_SYSTEM_TEMPLATES_PORTAL = "crm";

const fetchNotificationTypesPage = async (
  page: number,
  pageSize: number = PAGE_SIZE
): Promise<PaginatedSystemNotificationTypesResponse | undefined> => {
  const SYSTEM_TEMPLATES_ENDPOINT = `${EnvironmentVariables.REACT_APP_YMIR_URL}/v1/system-templates`;

  const apiUrl = brandUrl<SystemNotificationTypeDto[]>(
    SYSTEM_TEMPLATES_ENDPOINT
  );

  const response = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: { page, pageSize, portal: YMIR_SYSTEM_TEMPLATES_PORTAL },
    },
  });

  return response?.data;
};

export const getAllNotificationTypes = async (): Promise<
  SystemNotificationTypeDto[]
> => {
  let page = 1;
  let totalPages = Infinity;
  const allTemplates: SystemNotificationTypeDto[] = [];

  while (page <= totalPages) {
    const data = await fetchNotificationTypesPage(page);

    if (!data || !Array.isArray(data.data)) break;

    allTemplates.push(...data.data);
    totalPages = data.pages ?? 1;
    page++;
  }

  return allTemplates;
};

export const getMyNotifications = async (
  params: Partial<NotificationQueryParams> = {}
): Promise<NotificationResponseDto> => {
  const baseUrl = `${EnvironmentVariables.REACT_APP_YMIR_URL}/v1/my-notifications`;

  const apiUrl = brandUrl<NotificationResponseDto>(baseUrl);

  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value != null)
  );

  const response = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      ...(Object.keys(filteredParams).length && { params: filteredParams }),
    },
  });

  return response.data;
};

export const markAllNotificationsReadStatus = async (
  read: boolean
): Promise<void> => {
  const baseUrl = `${EnvironmentVariables.REACT_APP_YMIR_URL}/v1/my-notifications`;

  const apiUrl = brandUrl<void>(baseUrl);

  await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      method: AxiosRequestConfigMethods.PATCH,
      isEncoded: false,
      data: { read },
    },
  });
};

export const setNotificationReadStatus = async (
  id: string,
  read: boolean
): Promise<void> => {
  const baseUrl = `${EnvironmentVariables.REACT_APP_YMIR_URL}/v1/my-notifications/${id}`;

  const apiUrl = brandUrl<void>(baseUrl);

  await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      method: AxiosRequestConfigMethods.PATCH,
      isEncoded: false,
      data: { read },
    },
  });
};
